import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Plus, Download, Filter, Search, X, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AutoTestLogTable } from '@/components/tables/production/auto-test-log-table';
import { autoTestLogApi } from '@/api/production/logs';
import { useDebounce } from '@/hooks/use-debounce';
import { DateRangeInput } from '@/components/ui/date-range-input';
import type { DateRange } from 'react-day-picker';

export function AutoTestLogListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchValue, setSearchValue] = useState(searchParams.get('query') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [isPending, setIsPending] = useState(false);

  // Applied filter states (used in query)
  const [iccid, setIccid] = useState(searchParams.get('iccid') || '');
  const [status, setStatus] = useState(searchParams.get('status') || '');
  const [user, setUser] = useState(searchParams.get('user') || '');
  const [dateRange, setDateRange] = useState<DateRange>({
    from: searchParams.get('from') ? new Date(searchParams.get('from')!) : undefined,
    to: searchParams.get('to') ? new Date(searchParams.get('to')!) : undefined,
  });

  // Temporary filter states (used in modal before applying)
  const [tempIccid, setTempIccid] = useState(iccid);
  const [tempStatus, setTempStatus] = useState(status);
  const [tempUser, setTempUser] = useState(user);

  const page = Number(searchParams.get('page')) || 1;
  const limit = 20;

  // Debounced search to avoid too many API calls (only for main search input)
  const debouncedSearch = useDebounce(searchValue, 300);

  // Sync temporary values with applied values when modal opens
  useEffect(() => {
    if (showFilters) {
      setTempIccid(iccid);
      setTempStatus(status);
      setTempUser(user);
    }
  }, [showFilters, iccid, status, user]);

  const { data: logs, isLoading } = useQuery({
    queryKey: [
      'auto-test-logs',
      {
        query: debouncedSearch,
        iccid,
        status,
        user,
        from: dateRange.from?.toISOString(),
        to: dateRange.to?.toISOString(),
        page,
      },
    ],
    queryFn: () =>
      autoTestLogApi.findMany({
        query: debouncedSearch || undefined,
        iccid: iccid || undefined,
        status: status || undefined,
        user: user || undefined,
        from: dateRange.from?.toISOString(),
        to: dateRange.to?.toISOString(),
        page,
        limit,
      }),
  });

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  const handleSearch = () => {
    setIsPending(true);
    // Apply temporary filter values to actual filter state
    setIccid(tempIccid);
    setStatus(tempStatus);
    setUser(tempUser);

    const newParams = new URLSearchParams();
    if (searchValue) newParams.set('query', searchValue);
    if (tempIccid) newParams.set('iccid', tempIccid);
    if (tempStatus) newParams.set('status', tempStatus);
    if (tempUser) newParams.set('user', tempUser);
    if (dateRange.from) newParams.set('from', dateRange.from.toISOString());
    if (dateRange.to) newParams.set('to', dateRange.to.toISOString());
    newParams.set('page', '1'); // Reset to first page on search
    setSearchParams(newParams);
    setTimeout(() => setIsPending(false), 300);
  };

  const handleClearFilters = () => {
    setSearchValue('');
    setIccid('');
    setStatus('');
    setUser('');
    setTempIccid('');
    setTempStatus('');
    setTempUser('');
    setDateRange({ from: undefined, to: undefined });
    setSearchParams({});
  };

  const handlePageChange = (newPage: number) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', String(newPage));
    setSearchParams(newParams);
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export clicked');
  };

  const hasActiveFilters = iccid || status || user || dateRange.from || dateRange.to;
  const shouldShowResetButton = searchValue || hasActiveFilters;

  return (
    <div>
      <div className="border-b border-gray-900/10 pb-6 flex flex-wrap items-center gap-6 px-4 sm:flex-nowrap sm:px-6 lg:px-8">
        <div>
          <h1 className="text-base font-semibold leading-7 text-gray-900">Log de auto teste</h1>
          <p className="mt-2 text-sm text-gray-700">
            Uma lista de todos os registros de auto teste.
          </p>
        </div>

        <Link
          to="/production/tool/auto-test"
          className="ml-auto flex items-center gap-x-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
          title="Faça um novo auto teste"
        >
          <Plus className="-ml-1.5 h-5 w-5" aria-hidden="true" />
          Novo Auto Teste
        </Link>
      </div>

      <div className="border-b border-gray-900/10 pb-6 mt-6 px-4 sm:px-6 lg:px-8">
        <div className="w-full flex justify-between items-end">
          <div className="flex items-center gap-2">
            <Input
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Serial, IMEI, usuário ou tecnologia"
              className="w-60"
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />

            <DateRangeInput value={dateRange} onChange={setDateRange} showTimeInputs />

            <Button
              disabled={isPending}
              type="button"
              size="icon"
              variant="outline"
              onClick={() => setShowFilters(true)}
              className="!h-10 !w-10 !min-w-10 flex-shrink-0"
            >
              <Filter className="h-4 w-4" />
            </Button>

            <Button
              disabled={isPending}
              type="submit"
              size="icon"
              variant="outline"
              onClick={handleSearch}
              className="!h-10 !w-10 !min-w-10 flex-shrink-0"
            >
              {isPending ? (
                <Loader className="animate-spin h-4 w-4" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>

            {shouldShowResetButton && (
              <Button
                type="button"
                size="icon"
                variant="ghost"
                onClick={handleClearFilters}
                disabled={isPending}
                className="!h-10 !w-10 !min-w-10 flex-shrink-0"
              >
                {isPending ? <Loader className="animate-spin h-4 w-4" /> : <X className="h-4 w-4" />}
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            type="button"
            title="Exporte os dados filtrados"
            onClick={handleExport}
            className="ml-4"
          >
            <Download className="h-5 w-5 mr-2" /> Exportar
          </Button>
        </div>
      </div>

      <div className="mt-6 px-4 sm:px-6 lg:px-8">
        {isLoading ? (
          <div className="w-full text-center py-8">
            <p className="text-gray-500">Carregando...</p>
          </div>
        ) : (
          <>
            <AutoTestLogTable data={logs?.docs || []} />

            {(logs?.pages ?? 1) > 1 && (
              <div className="mt-4 overflow-hidden rounded-md border">
                <Pagination
                  currentPage={page}
                  totalPages={logs?.pages ?? 1}
                  totalItems={logs?.total ?? 0}
                  limit={limit}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Filter Dialog */}
      <Dialog open={showFilters} onOpenChange={setShowFilters}>
        <DialogContent className="sm:max-w-[30rem] p-0">
          <DialogHeader className="modal-header">
            <DialogTitle>Filtros adicionais</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4 p-6">
            <div className="flex flex-col gap-1">
              <Label htmlFor="iccid">ICCID</Label>
              <Input
                id="iccid"
                value={tempIccid}
                onChange={(e) => setTempIccid(e.target.value)}
                placeholder="Digite o ICCID"
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="status">Status</Label>
              <Select value={tempStatus} onValueChange={setTempStatus}>
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Sucesso</SelectItem>
                  <SelectItem value="false">Falha</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="user">Usuário</Label>
              <Input
                id="user"
                value={tempUser}
                onChange={(e) => setTempUser(e.target.value)}
                placeholder="Nome do usuário"
              />
            </div>
          </div>

          <DialogFooter className="p-6 border-t">
            <Button
              onClick={() => {
                handleSearch();
                setShowFilters(false);
              }}
              disabled={isPending}
            >
              {isPending ? (
                <>
                  <Loader className="animate-spin" size={12} /> Filtrando
                </>
              ) : (
                <span>Filtrar</span>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
